/* BOMBA del ESPEJO de European Athletics (Europeo Birmingham 2026, 2026-08-07).
 *
 * EA bloquea las IPs de GCP (Cloudflare) → el motor automático no puede leer
 * su API desde la nube. Este módulo corre EN LA PESTAÑA /telegram del admin
 * (IP residencial): descarga los feeds de EA y los POSTea a /api/ea-mirror
 * (whitelist), que los escribe en eaMirror/* de Firestore; de ahí lee el
 * motor (auto_wa fuente="ea" → scraper_ea_live.py).
 *
 * Es una bomba TONTA a propósito: sin lógica de publicación (vive toda en el
 * motor). Solo espeja lo que cambia (hash por unidad) para no gastar writes.
 * Wake Lock + indicador de último bombeo van en la UI (AtletaEnVivo).
 */

import { auth } from '@/lib/firebase';

const API = 'https://api.european-athletics.com/trpc/';
const CICLO_MS = 120_000;         // 2 min (2026-08-10: reducción de carga a EA
                                  // tras su aviso de bloqueo). El tick del motor
                                  // también corre cada 2 min, así que no se pierde
                                  // frescura útil, y se barre ~3× menos.
const ATLETAS_CADA_MS = 30 * 60_000;
const EVENTOS_CADA_MS = 5 * 60_000;   // refresco del meta aunque nada cambie
const LOTE_UNIDADES = 3;          // ráfagas pequeñas: Cloudflare corta las grandes

export interface EstadoBomba {
  activa: boolean;
  ultimoCiclo: number | null;     // epoch ms del último SONDEO completado (cada 45 s):
                                  // el chivato de vida — "último envío" solo se mueve
                                  // cuando hay datos nuevos que mandar
  ultimoBombeo: number | null;    // epoch ms del último POST con éxito
  unidadesEspejadas: number;      // acumulado de unidades escritas
  error: string | null;
}

interface UnidadMeta {
  key: string; nombre: string; ronda: string;
  fecha: string; hora: string; estado: string;
  resumen?: boolean;   // EA ya resolvió la RONDA de esta unidad (SUMMARY)
}

async function trpc<T>(proc: string, payload: Record<string, string>): Promise<T> {
  const u = `${API}${proc}?input=${encodeURIComponent(JSON.stringify({ json: payload }))}`;
  const r = await fetch(u);
  if (!r.ok) throw new Error(`${proc} ${r.status}`);
  return (await r.json()).result.data.json as T;
}

/** Hash rápido (FNV-1a) del JSON de una unidad — solo para detectar cambios. */
function hashDe(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

export class BombaEspejo {
  private timer: number | null = null;
  private hashes = new Map<string, string>();
  private ultimoAtletas = 0;
  private ultimoEventos = 0;
  private firmaEventos = '';
  // Barrido de estados (headers, ~50 requests a EA): se refresca cada
  // EVENTOS_CADA_MS y se reutiliza entre medias (2026-08-10: reducción de carga
  // a EA). Los RESULTADOS de las unidades activas sí van cada ciclo.
  private ultimoBarrido = 0;
  private ultimosEventos: UnidadMeta[] = [];
  estado: EstadoBomba = { activa: false, ultimoCiclo: null, ultimoBombeo: null, unidadesEspejadas: 0, error: null };

  constructor(
    private code: string,
    private comp: string,
    private eventos: () => Promise<UnidadMeta[]>,
    private onCambio: (e: EstadoBomba) => void,
  ) {}

  arrancar(): void {
    if (this.timer !== null) return;
    this.estado = { ...this.estado, activa: true, error: null };
    this.onCambio(this.estado);
    void this.ciclo();
    this.timer = window.setInterval(() => void this.ciclo(), CICLO_MS);
  }

  parar(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    this.estado = { ...this.estado, activa: false };
    this.onCambio(this.estado);
  }

  private async ciclo(): Promise<void> {
    try {
      // Headers de estado (~50 requests) SOLO cada EVENTOS_CADA_MS; entre medias
      // se reutiliza el último meta (2026-08-10: menos carga a EA). Los feeds de
      // resultados de las unidades activas se piden igual cada ciclo (abajo).
      const refrescaMeta = this.ultimosEventos.length === 0
        || Date.now() - this.ultimoBarrido > EVENTOS_CADA_MS;
      const eventos = refrescaMeta ? await this.eventos() : this.ultimosEventos;
      if (refrescaMeta) { this.ultimosEventos = eventos; this.ultimoBarrido = Date.now(); }
      // Solo se espejan unidades CON ACTIVIDAD (2026-08-10: reducción de carga a
      // EA tras su aviso). Antes se pedían también las de HOY y MAÑANA aún no
      // empezadas (para las startlists del horario −45'), pero eso generaba un
      // 404 por cada unidad inexistente —lo que saturaba su infra— y el horario
      // pasa a MANUAL, así que ya no hacen falta esas startlists.
      const conFeed = eventos.filter((e) =>
        ['Empezada', 'En curso', 'Extraoficial', 'Oficial'].includes(e.estado));
      const unidades: Record<string, string> = {};
      for (let i = 0; i < conFeed.length; i += LOTE_UNIDADES) {
        const lote = conFeed.slice(i, i + LOTE_UNIDADES);
        await Promise.all(lote.map(async (e) => {
          const uid = e.key.split(':', 3)[2];
          // Una unidad ya espejada OFICIAL no cambia más: no re-descargar.
          const sello = this.hashes.get(uid);
          if (sello?.startsWith('FIN|')) return;
          try {
            const raw = await trpc<{ athletes?: Record<string, unknown>[] }>(
              'liveResults.getRaceResultsFeed',
              { event: uid.toLowerCase(), competitionCode: this.code });
            // Sin intermediates: los parciales de maratón/marcha inflan el
            // doc y el motor no los usa.
            const limpio = {
              ...raw,
              athletes: (raw.athletes ?? []).map((a) => {
                const { intermediates: _i, ...resto } = a as { intermediates?: unknown };
                return resto;
              }),
            };
            const s = JSON.stringify(limpio);
            const h = (e.estado === 'Oficial' ? 'FIN|' : '') + hashDe(s);
            if (h !== sello) {
              unidades[uid] = s;
              this.hashes.set(uid, h);
            }
          } catch { /* transitorio: siguiente ciclo */ }
        }));
      }

      const body: Record<string, unknown> = { code: this.code, comp: this.comp };
      if (Object.keys(unidades).length) body.unidades = unidades;

      // `resumen` entra en la firma: EA puede publicar el SUMMARY de una ronda
      // sin que cambie el estado de ninguna unidad (ya están todas Oficiales).
      // Sin esto, el motor tardaría hasta EVENTOS_CADA_MS en enterarse.
      const firma = eventos
        .map((e) => `${e.key}|${e.estado}|${e.hora}|${e.resumen ? 1 : 0}`)
        .join(';');
      if (firma !== this.firmaEventos || Date.now() - this.ultimoEventos > EVENTOS_CADA_MS
          || Object.keys(unidades).length) {
        body.eventos = eventos;
        this.firmaEventos = firma;
        this.ultimoEventos = Date.now();
      }

      if (Date.now() - this.ultimoAtletas > ATLETAS_CADA_MS) {
        const d = await trpc<{ athletes?: { athleteId: string; firstName?: string; lastName?: string; countryCode?: string; entries?: { eventId?: string }[] }[] }>(
          'liveResults.getAthletesFeed', { competitionCode: this.code });
        const dicc: Record<string, { n: string; p: string | null; e?: string[] }> = {};
        for (const a of d.athletes ?? []) {
          const apellido = (a.lastName ?? '').trim().toLowerCase()
            .replace(/(^|[\s-])\S/g, (c) => c.toUpperCase());
          const pais = (a.countryCode ?? '').trim() || null;
          dicc[String(a.athleteId)] = {
            n: `${(a.firstName ?? '').trim()} ${apellido}`.trim(),
            p: pais,
            // Pruebas inscritas SOLO de los ESP: respaldo del horario de
            // −45' cuando la startlist de una unidad aún no está publicada.
            ...(pais === 'ESP' ? {
              e: (a.entries ?? []).map((x) => (x.eventId ?? '').slice(0, 22).toUpperCase())
                .filter(Boolean),
            } : {}),
          };
        }
        body.atletas = dicc;
        this.ultimoAtletas = Date.now();
      }

      // Nada nuevo y meta fresco: ciclo sin POST (ahorra writes).
      if (!body.unidades && !body.eventos && !body.atletas) {
        this.estado = { ...this.estado, ultimoCiclo: Date.now(), error: null };
        this.onCambio(this.estado);
        return;
      }

      const u = auth.currentUser;
      if (!u) throw new Error('sin sesión de Firebase');
      const r = await fetch('/api/ea-mirror', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await u.getIdToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`ea-mirror ${r.status}: ${(await r.text()).slice(0, 120)}`);
      const res = (await r.json()) as { unidades?: number };
      this.estado = {
        ...this.estado,
        ultimoCiclo: Date.now(),
        ultimoBombeo: Date.now(),
        unidadesEspejadas: this.estado.unidadesEspejadas + (res.unidades ?? 0),
        error: null,
      };
      this.onCambio(this.estado);
    } catch (err) {
      this.estado = { ...this.estado, error: err instanceof Error ? err.message : String(err) };
      this.onCambio(this.estado);
    }
  }
}
